import { reportApplyEntityGrouping, reportSetEntityGrouping } from 'redux-v2/report/report-store.reducer';
import { getMetaSelectorOptions } from 'redux-v2/report/report-store.requests';
import { getMetaSelectorOptions as getMetaSelectorOptionsBuilder } from 'redux-v2/report-builder/report-builder-store.requests';
import { reportBuilderApplyEntityGrouping, reportBuilderDimensionEntityGrouping } from 'redux-v2/report-builder/report-builder-store.reducer';
import { RootStateOrAny } from 'react-redux';
import type { AnyAction } from 'redux';
import { SIERAPage } from 'core/constants/page-url';
import { metaFieldOptionsSelector, reportIdSelector } from 'redux-v2/report/report-store.selectors';
import { metaFieldOptionsSelector as metaFieldOptionsSelectorBuilder, reportConfigIdSelector } from 'redux-v2/report-builder/report-builder-store.selectors';
import { IDimension } from 'core/models/report-redux';
import {
  IColumn, IDimensionMetaField, ISetDimensionsEntityGroupingPayload, ISetMetaSelectorOptionsPayload,
} from 'core/models/report-response';
import {
  BuilderMode, ColumnModes, DimensionMode, DimensionStatus,
} from 'core/constants/report';
import { isValidArrayAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import { getDimensionProps } from 'redux-v2/report-builder/report-builder-store.utils';
import ReportBuilderService from 'services/report-builder.service';

export interface EntityGroupingActions {
  getMetaAction: (params: any) => any;
  setGroupingAction: (params: any) => AnyAction;
  updateGroupingAction: (params: any) => AnyAction;
}

export interface EntityGroupingSelectors {
  metaFieldSelector: (state: RootStateOrAny) => Record<string, any[]>;
  reportIdSelector: (state: RootStateOrAny) => string;
}

/**
 * Creates store-agnostic implementations for entity grouping
 *
 * Architecture:
 * - Abstracts differences between Report and ReportBuilder stores
 * - Provides unified interface for:
 *   1. Action creators (get, set, update)
 *   2. Selectors (metadata, report ID)
 */
export const createEntityGroupingSelectors = (selectedPage: SIERAPage): EntityGroupingSelectors => {
  switch (selectedPage) {
    case SIERAPage.REPORT:
      return {
        metaFieldSelector: (params) => metaFieldOptionsSelector(params),
        reportIdSelector: (params) => reportIdSelector(params),
      };
    case SIERAPage.REPORTBUILDER:
    case SIERAPage.REPORTEDIT:
      return {
        metaFieldSelector: (params) => metaFieldOptionsSelectorBuilder(params),
        reportIdSelector: (params) => reportConfigIdSelector(params),
      };
    default:
      // this case should never happen ideally
      console.error(`Unsupported page: ${selectedPage}`);
      // fallback to default selectors
      return {
        metaFieldSelector: (params) => metaFieldOptionsSelectorBuilder(params),
        reportIdSelector: (params) => reportConfigIdSelector(params),
      };
  }
};

export const createEntityGroupingActions = (selectedPage: SIERAPage): EntityGroupingActions => {
  switch (selectedPage) {
    case SIERAPage.REPORT:
      return {
        getMetaAction: (params) => getMetaSelectorOptions(params),
        setGroupingAction: (params) => reportApplyEntityGrouping(params),
        updateGroupingAction: (params) => reportSetEntityGrouping(params),
      };
    case SIERAPage.REPORTBUILDER:
    case SIERAPage.REPORTEDIT:
      return {
        getMetaAction: (params) => getMetaSelectorOptionsBuilder(params),
        setGroupingAction: (params) => reportBuilderApplyEntityGrouping(params),
        updateGroupingAction: (params) => reportBuilderDimensionEntityGrouping(params),
      };
    default:
      // this case should never happen ideally
      console.error(`Unsupported page: ${selectedPage}`);
      // fallback to default selectors
      return {
        getMetaAction: (params) => getMetaSelectorOptionsBuilder(params),
        setGroupingAction: (params) => reportBuilderApplyEntityGrouping(params),
        updateGroupingAction: (params) => reportBuilderDimensionEntityGrouping(params),
      };
  }
};

/**
 * Creates a dimension configuration for entity grouping.
 * Handles field switching, meta field references, and dimension modes.
 *
 * @param dimension - Base dimension configuration
 * @param column - Selected column configuration
 * @param status - Application status for the dimension
 * @param isMetaColumnPresent - Whether meta column exists
 * @param mode - Optional dimension mode override
 * @returns Configured dimension object
 */
export const createEntityGroupedDimension = (
  dimension: IDimension,
  column: IColumn,
  status: DimensionStatus,
  isMetaColumnPresent: boolean,
  mode?: DimensionMode,
): IDimension => {
  const isColumnAlreadyApplied = dimension.Applied === DimensionStatus.Applied;
  let id: string;
  if (mode === undefined) {
    id = dimension.Id; // update case - use original ID
  } else {
    id = column.Id; // create case - use meta selector column ID
  }
  let referTo: string | null = null;
  if (isMetaColumnPresent) {
    referTo = mode === undefined
      ? getMetaFieldId(dimension.Id)
      : column?.Props?.ReferTo ?? null;
  }
  // Keep standalone display columns visible by default only meta columns or user hidden columns should remain hidden.
  const resolvedMode = !referTo && (column.Props?.Mode ?? ColumnModes.Hidden) === ColumnModes.Hidden
    ? ColumnModes.Sortable
    : column.Props?.Mode;

  return {
    ...dimension,
    Id: id,
    Mode: resolvedMode,
    Applied: status,
    DimensionMode: mode ?? dimension.DimensionMode,
    ReferTo: referTo,
    BuilderConfig: {
      ...dimension.BuilderConfig,
      IsDynamicField: isColumnAlreadyApplied ? dimension.BuilderConfig.IsDynamicField : column?.BuilderConfig?.IsDynamicField,
    },
    Props: {
      ...dimension.Props,
      ReferTo: referTo,
      Mode: resolvedMode,
      IsDimensionMetaField: column?.Props?.IsDimensionMetaField,
      IsMetaFieldSelector: column.Props?.IsMetaFieldSelector,
      MetaFieldSelectorDisplayName: column.Props?.MetaFieldSelectorDisplayName,
      MetaFieldSelectorID: column.Props?.MetaFieldSelectorID,
      MetaFieldSelectorSchemaNames: column.Props?.MetaFieldSelectorSchemaNames,
      IsColGroupingAllowed: column.Props?.IsColGroupingAllowed,
    },
  };
};

/**
 * Creates a meta dimension configuration for entity grouping.
 * Links the meta dimension to its parent dimension.
 *
 * @param id - Parent dimension ID
 * @param metaColumnConfig - Meta column configuration
 * @returns Configured meta dimension object
 */
export const createEntityGroupMetaDimension = (
  dimension: IDimension,
  metaColumnConfig: IColumn,
): IDimension => ({
  Name: dimension.Name,
  ...metaColumnConfig.Props.Dimension,
  IsMasked: metaColumnConfig.Props?.IsMasked,
  Mode: metaColumnConfig.Props?.Mode,
  Id: getMetaFieldId(dimension.Id),
  Props: {
    ...metaColumnConfig.Props,
    ParentDimension: dimension.Id,
  },
});

/**
 * Adds a dimension to the grouping configuration at the specified index.
 * Handles both regular dimensions and field-switched dimensions.
 *
 * @param appliedDimensions - Current array of applied dimensions
 * @param dimension - The dimension to be added
 * @param selectedColumn - The selected column configuration
 * @param index - Position where dimension should be inserted
 * @param isMetaColumnPresent - Whether a meta column exists for this dimension
 * @returns Updated array of applied dimensions
 */
export const addDimensionToGrouping = (
  appliedDimensions: IDimension[],
  dimension: IDimension,
  selectedOption: IDimensionMetaField,
  index: number,
  isMetaColumnPresent: boolean,
  isReportPage:boolean,
): IDimension[] => {
  const newAppliedDimensions = appliedDimensions;
  if (dimension?.BuilderConfig?.IsUserDataType) {
    // Find index of existing dimension with same ID (must exist)
    const existingIndex = newAppliedDimensions.findIndex((d) => d?.Id === dimension?.Id);
    if (existingIndex === -1) {
      // this case should never execute but is here for completeness
      console.warn(`Dimension with ID ${dimension?.Id} not found in applied dimensions`);
      return newAppliedDimensions;
    }
    newAppliedDimensions.splice(existingIndex, 1);
  }
  newAppliedDimensions.splice(index, 0, dimension);
  if (isMetaColumnPresent && !isReportPage) {
    const metaDimensionIndex = newAppliedDimensions.findIndex((dim) => dim?.Props?.ParentDimension === dimension.Id);
    const metaDimension: IDimension = createEntityGroupMetaDimension(
      dimension,
      selectedOption.Config[0],
    );
    if (metaDimensionIndex === -1) {
      newAppliedDimensions.push(metaDimension);
    } else {
      newAppliedDimensions[metaDimensionIndex] = metaDimension;
    }
  }
  return newAppliedDimensions;
};

/**
 * Updates an entity grouped dimension with new meta field selection.
 * Handles column grouping restrictions and meta field updates.
 *
 * @param appliedIndex - Index of dimension to update
 * @param newAppliedDimensions - Current array of applied dimensions
 * @param selectedOption - Selected meta field option
 * @returns Updated array of applied dimensions
 */
export const updateEntityGroupedDimension = (
  appliedIndex: number,
  newAppliedDimensions: IDimension[],
  selectedOption: IDimensionMetaField,
  isReportPage:boolean,
): IDimension[] => {
  const updatedDimensions = [...newAppliedDimensions];
  const currentDimension = updatedDimensions[appliedIndex];
  // column grouping is not allowed for dimensions meeting below criteria
  if (currentDimension.DimensionMode === DimensionMode.ColumnGroup
    && isValidArrayAndNotEmpty(selectedOption?.Config)
    && !isColGroupingAllowed(selectedOption.Config[0])) {
    // TO DO: Raise error message to UI
    return updatedDimensions;
  }
  const column = selectedOption.Config.at(-1);
  if (!currentDimension || !column) {
    return updatedDimensions; // this should ideally never happen
  }
  const isMetaColumnPresent = selectedOption.Config.length > 1;
  const metaDimensionIndex = updatedDimensions.findIndex((dim) => dim?.Props?.ParentDimension === currentDimension.Id);
  // Preserve the user's hide choice across a meta selection change
  const wasUserHidden = isColumnUserHidden(currentDimension, updatedDimensions);
  // always update the primary dimension at appliedIndex
  updatedDimensions[appliedIndex] = createEntityGroupedDimension(
    currentDimension,
    column,
    DimensionStatus.Applied,
    isMetaColumnPresent,
  );
  if (isMetaColumnPresent && !isReportPage) {
    const metaDimension: IDimension = createEntityGroupMetaDimension(
      currentDimension,
      selectedOption.Config[0],
    );
    // keep the column hidden across a meta selection change
    if (wasUserHidden) {
      metaDimension.Mode = ColumnModes.Hidden;
      metaDimension.Props.Mode = ColumnModes.Hidden;
    }
    if (metaDimensionIndex === -1) {
      updatedDimensions.push(metaDimension);
    } else {
      updatedDimensions[metaDimensionIndex] = metaDimension;
    }
  } else {
    // the primary itself is the displayed column
    if (wasUserHidden) {
      updatedDimensions[appliedIndex].Mode = ColumnModes.Hidden;
      updatedDimensions[appliedIndex].Props.Mode = ColumnModes.Hidden;
    }
    if (metaDimensionIndex !== -1) {
      updatedDimensions.splice(metaDimensionIndex, 1);
    }
  }
  return updatedDimensions;
};

/**
 * Creates a drilldown configuration for entity grouped dimensions.
 *
 * @param currentColumn - Base column configuration
 * @param retrievedColumn - Selected column configuration retrieved from get-meta-selector-options api
 * @param status - Application status
 * @returns Configured drilldown column
 */
export const createEntityGroupedDrilldown = (
  currentColumn: IColumn,
  retrievedColumn: IColumn,
  status: DimensionStatus,
): IColumn => {
  const metaFieldProps = {
    IsDimensionMetaField: retrievedColumn?.Props?.IsDimensionMetaField,
    IsMetaFieldSelector: retrievedColumn?.Props?.IsMetaFieldSelector,
    MetaFieldSelectorDisplayName: retrievedColumn?.Props?.MetaFieldSelectorDisplayName,
    MetaFieldSelectorID: retrievedColumn?.Props?.MetaFieldSelectorID,
    MetaFieldSelectorSchemaNames: retrievedColumn?.Props?.MetaFieldSelectorSchemaNames,
    IsColGroupingAllowed: retrievedColumn?.Props?.IsColGroupingAllowed,
  };
  return {
    ...currentColumn,
    Name: retrievedColumn?.Name,
    Id: retrievedColumn?.Id,
    Props: {
      ...currentColumn?.Props,
      ...metaFieldProps,
      Dimension: {
        ...currentColumn?.Props?.Dimension,
        Applied: status,
      },
    },
  };
};

/**
 * Updates entity grouped drilldown configuration with new meta field selection.
 *
 * @param appliedIndex - Index of drilldown to update
 * @param newAppliedDrilldowns - Current array of applied drilldowns
 * @param selectedOption - Selected meta field option
 * @returns Updated array of applied drilldowns
 */
export const updateEntityGroupedDrilldown = (
  appliedIndex: number,
  newAppliedDrilldowns: IColumn[],
  selectedOption: IDimensionMetaField,
): IColumn[] => {
  if (!isValidArrayAndNotEmpty(selectedOption?.Config)) {
    return newAppliedDrilldowns;
  }
  const column = selectedOption.Config.at(-1);
  const currentDrilldown = newAppliedDrilldowns[appliedIndex];
  const metaFieldProps = {
    IsDimensionMetaField: column?.Props?.IsDimensionMetaField,
    IsMetaFieldSelector: column?.Props?.IsMetaFieldSelector,
    MetaFieldSelectorDisplayName: column?.Props?.MetaFieldSelectorDisplayName,
    MetaFieldSelectorID: column?.Props?.MetaFieldSelectorID,
    MetaFieldSelectorSchemaNames: column?.Props?.MetaFieldSelectorSchemaNames,
    IsColGroupingAllowed: column?.Props?.IsColGroupingAllowed,
  };

  const updatedDrilldown: IColumn = {
    ...currentDrilldown,
    Props: {
      ...currentDrilldown?.Props,
      ...metaFieldProps,
      Dimension: {
        ...currentDrilldown?.Props?.Dimension,
        Applied: DimensionStatus.Applied,
      },
    },
  };
  newAppliedDrilldowns[appliedIndex] = updatedDrilldown;
  return newAppliedDrilldowns;
};

/**
 * Creates a column configuration for entity grouping.
 *
 * @param dimension - Base dimension configuration
 * @param column - Selected column configuration
 * @param isMetaColumnRequired - Whether meta column is required
 * @returns Configured column object
 */
export const createEntityGroupedColumn = (
  dimension: IDimension,
  column: IColumn,
  isMetaColumnRequired: boolean,
): IColumn => ({
  Id: dimension.Id,
  Name: dimension.Name,
  BuilderConfig: dimension?.BuilderConfig || column?.BuilderConfig,
  Props: {
    ...dimension.Props,
    Dimension: getDimensionProps(dimension, dimension.Props),
    ReferTo: isMetaColumnRequired ? getMetaFieldId(dimension.Id) : null,
    Mode: dimension.Mode,
    IsAdditional: dimension?.Props?.IsAdditional || false,
    QueryId: dimension?.Props?.QueryId || '',
    IsMetaFieldSelector: column.Props?.IsMetaFieldSelector,
    MetaFieldSelectorDisplayName: column.Props?.MetaFieldSelectorDisplayName,
    MetaFieldSelectorID: column.Props?.MetaFieldSelectorID,
    MetaFieldSelectorSchemaNames: column.Props?.MetaFieldSelectorSchemaNames,
    IsColGroupingAllowed: column.Props?.IsColGroupingAllowed,
  },
});

/**
 * Generates entity grouping store from dimensions.
 * Maps dimension IDs to their selected meta field IDs.
 *
 * @param dimensions - Array of dimensions or columns
 * @param entityGroupStore - Current entity group store
 * @returns Updated entity group store
 */
export const generateEntityStore = (dimensions: IDimension[] | IColumn[], entityGroupStore: Record<string, string>): Record<string, string> => {
  const updatedEntityGroupStore = { ...entityGroupStore };
  if (isValidArrayAndNotEmpty(dimensions)) {
    dimensions?.forEach((dimension) => {
      if (dimension?.Props?.Dimension?.Applied === DimensionStatus.Applied && isEntityGrouped(dimension) && dimension?.Props?.MetaFieldSelectorID) {
        updatedEntityGroupStore[dimension.Id] = dimension.Props.MetaFieldSelectorID;
      }
    });
  }
  return updatedEntityGroupStore;
};

/**
 * Updates entity group store with new meta field selection.
 *
 * @param store - Current entity group store
 * @param item - Item to update
 */
export const updateEntityGroupStore = (store: Record<string, string>, item: IColumn | IDimension) => {
  if (item?.Id && item?.Props?.MetaFieldSelectorID) {
    store[item.Id] = item.Props.MetaFieldSelectorID;
  }
};

/**
 * Updates meta selector options in the entity group options map.
 *
 * @param payload - Payload containing update data and response
 * @param entityGroupOptionsMap - Current entity group options map
 * @returns Updated entity group options map
 */
export const getUpdatedMetaSelectorOptions = (payload: ISetMetaSelectorOptionsPayload, entityGroupOptionsMap: Record<string, IDimensionMetaField[]>): Record<string, IDimensionMetaField[]> => {
  const { data, res, isCreate } = payload;
  if (isValidMetaSelectorOptions(res)) {
    const selectedOption = res[0];
    const id = isCreate ? selectedOption.Config.at(-1)?.Id : data?.Id;
    entityGroupOptionsMap[id] = res;
  }
  return entityGroupOptionsMap;
};

/**
 * Gets the index of the original dimension in the applied dimensions.
 *
 * @param appliedDimensions - applied dimensions
 * @param dimension - Dimension to find
 * @param isDrilldown - whether this is a drilldown operation (defaults to false)
 * @returns Index of the original dimension
 */
export const getOriginalDimensionIndex = (
  appliedDimensions: IDimension[] | IColumn[],
  dimension: IDimension | IColumn,
  isDrilldown: boolean = false,
): number => appliedDimensions.findIndex((dim) => dim?.Id !== dimension?.Id
    && dim?.BuilderConfig?.Alias === dimension?.BuilderConfig?.Alias
    && dim?.Props?.Namespace === dimension?.Props?.Namespace
    && dim?.BuilderConfig?.JoinID === dimension?.BuilderConfig?.JoinID
    && (!isDrilldown || (isDrilldown && dim?.Props?.Dimension?.Applied === DimensionStatus.NotApplied)));

/**
 * Validates if meta selector options are valid.
 *
 * @param res - Meta selector options to validate
 * @returns True if options are valid
 */
export const isValidMetaSelectorOptions = (res: IDimensionMetaField[]): boolean => isValidArrayAndNotEmpty(res) && isValidArrayAndNotEmpty(res[0]?.Config);

/**
 * Checks if a column is entity grouped.
 *
 * @param column - Column to check
 * @returns True if column is entity grouped
 */
export const isEntityGrouped = (column: IColumn | IDimension): boolean => column?.Props?.IsMetaFieldSelector;

/**
 * Checks if column grouping is allowed for an entity grouped column.
 *
 * @param column - Column to check
 * @returns True if column grouping is allowed
 */
export const isColGroupingAllowed = (column: IColumn | IDimension): boolean => isEntityGrouped(column) && column?.Props?.IsColGroupingAllowed;

/**
 * Generates a meta field ID from a dimension ID.
 *
 * @param id - Dimension ID
 * @returns Meta field ID
 */
export const getMetaFieldId = (id: string): string => `${id}_meta`;

/**
 * Checks if drilldown tab is active.
 *
 * @param activeId - Active tab ID
 * @returns True if drilldown tab is active
 */
export const isDrilldownTabActive = (activeId: string | undefined): boolean => activeId !== undefined;

/**
 * Gets the builder mode based on active tab.
 *
 * @param activeId - Active tab ID
 * @returns Builder mode
 */
export const getBuilderMode = (activeId: string | undefined): BuilderMode => (isDrilldownTabActive(activeId) ? BuilderMode.Drilldown : BuilderMode.Dimensions);

/**
 * Fetches meta selector options for a dimension.
 *
 * @param params - Payload containing dimension data and report ID
 * @returns Promise resolving to meta selector options
 */
export const fetchMetaSelectorOptions = async (
  params: ISetDimensionsEntityGroupingPayload,
): Promise<any> => {
  const {
    data, reportId, activeId, matchingDimension,
  } = params;
  const builderMode = getBuilderMode(activeId);
  const payload = {
    ReportId: reportId,
    BuilderMode: builderMode,
    Alias: data?.BuilderConfig?.Alias || '',
    Namespace: data?.Props?.Namespace || '',
    JoinId: data?.BuilderConfig?.JoinID,
    DisplayName: matchingDimension?.Name || data.BuilderConfig.DisplayName || data?.Name,
    IsDynamicField: data?.BuilderConfig?.IsDynamicField,
  };
  return new ReportBuilderService().getMetaSelectorOptions(payload);
};

// For a meta-field-selector, returns its ReferTo column (the one actually shown); otherwise returns the column itself.
export const getHideTargetColumn = <T extends IColumn | IDimension>(col: T, columns?: Array<IColumn | IDimension>): IColumn | IDimension | undefined => {
  const referToId = col?.Props?.ReferTo || (col as IDimension)?.ReferTo;
  if (referToId) {
    return columns?.find((c) => c?.Id === referToId);
  }
  return col;
};

// Hidden Column
export const isColumnUserHidden = (col: IColumn | IDimension, columns?: Array<IColumn | IDimension>): boolean => {
  const referToId = col?.Props?.ReferTo || (col as IDimension)?.ReferTo;
  if (referToId) {
    const target = columns?.find((c) => c?.Id === referToId);
    return !!target && target?.Props?.Mode === ColumnModes.Hidden;
  }
  return col?.Props?.Mode === ColumnModes.Hidden;
};
